import FormModal from "../components/form/FormModal";
import {useAppTranslation} from "../services/i18n";
import {partyName} from "../model/party";
import {JsonInvite, JsonInviteInfo, JsonInviteInfoInviteTypeEnum, JsonInviteInfoStatusEnum, JsonInviteReply, JsonInviteReplyStatusEnum} from "../generated-api";
import InviteReviewForm from "../components/invite/InviteReviewForm";
import CodebookValue from "../components/CodebookValue";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {fetchInvite, fetchInviteInfo, sendInvite} from "../store/invites";
import {getApiResult} from "../helpers/api";
import {fetchInviteReply} from "../store/inviteReplies";
import {useAppDispatch} from "../store";
import InviteModal from "./InviteModal";
import {FAKE_VALUE_EMPTY} from "../model/form";
import {inviteTypeAllOptions, isInviteUpdate} from "../model/invite";
import {addMessage} from "../store/localApp";
import {MailRounded} from "@mui/icons-material";
import {ModalProps, useModal} from "../services/modal";

type Props = {
    inviteId: number,
    onSave: (values: JsonInviteReply) => void
    onCancel: () => void,
}

const InviteReviewModal = (props: Props) => {
    const {inviteId} = props;

    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const modal = useModal();

    const [inviteWithReply, setInviteWithReply] = useState<{
        invite: JsonInviteInfo,
        reply: JsonInviteReply
    } | undefined>(undefined);
    const [editInvite, setEditInvite] = useState<JsonInvite | undefined>(undefined);

    const invite = inviteWithReply?.invite;
    const reply = inviteWithReply?.reply;

    const handleFetchInviteWithReply = useCallback(() => {
        if (!inviteId) {
            return;
        }
        dispatch(fetchInviteInfo(inviteId)).then((res) => {
            const invite = getApiResult<JsonInviteInfo>(res);
            if (!invite) {
                return;
            }
            if (!invite.replyId) {
                setInviteWithReply({invite, reply: {status: JsonInviteReplyStatusEnum.Draft}});
                return;
            }
            dispatch(fetchInviteReply(invite.replyId)).then((res) => {
                const reply = getApiResult(res);
                if (reply) {
                    setInviteWithReply({invite, reply});
                }
            });
        });

    }, [inviteId, dispatch]);

    const handleEditInvite = useCallback(async () => {
        if (invite?.inviteId) {
            setEditInvite(getApiResult(await dispatch(fetchInvite(invite.inviteId))));
        }
    }, [invite, dispatch]);

    const handleResendInvite = useCallback(async () => {
        if (invite?.inviteId) {
            const result = await modal.confirm({
                title: t('Potvrzení nového odeslání'),
                message: t('Skutečně znovu odeslat úvodní email na adresu "{{email}}"?', invite),
                confirmColor: 'success',
                confirmIcon: <MailRounded/>,
                confirmText: t('Znovu odeslat')
            } as ModalProps);
            if (result !== 'CONFIRM') {
                return false;
            }
            const res = await dispatch(sendInvite(invite as JsonInvite));
            if (getApiResult(res)) {
                dispatch(addMessage({
                    code: 'OK',
                    action: 'invite/send',
                    severity: 'success',
                    title: t('Úvodní email znovu odeslán na aktuální adresu "{{email}}"', invite)
                }));
                return true;
            }
        }
        return false;
    }, [invite, modal, dispatch, t]);

    const handleSaveInvite = useCallback(() => {
        handleFetchInviteWithReply();
        setEditInvite(undefined);
    }, [handleFetchInviteWithReply]);

    const handleCancelInvite = useCallback(() => {
        setEditInvite(undefined);
    }, []);

    useEffect(() => {
        handleFetchInviteWithReply();
    }, [handleFetchInviteWithReply])

    if (!invite || !reply) {
        return null;
    }
    if (reply.processData === undefined) {
        reply.processData = invite.inviteType === JsonInviteInfoInviteTypeEnum.TravelOrg
            ? {
                travels: []
            }
            : {
                extras: [],
                eventPartyDays: {
                    [invite.partyId as number]: {}
                }
            };
        if (invite.inviteType === JsonInviteInfoInviteTypeEnum.Supplier) {
            reply.replyData?.extras?.forEach((extra) => {
                if (!!extra.partyId && reply.processData?.extras && extra.extraId) {
                    if (extra.partyId !== FAKE_VALUE_EMPTY) {
                        reply.processData.extras[extra.extraId] = {...extra};
                    }
                }
            })
        }
        if (invite.inviteType === JsonInviteInfoInviteTypeEnum.Guest) {
            reply.replyData?.extras?.forEach((extra) => {
                if (!!extra.partyId && reply.processData?.extras && extra.extraId) {
                    if (extra.partyId !== FAKE_VALUE_EMPTY) {
                        reply.processData.extras[extra.extraId] = {...extra};
                    }
                }
            })
        }
    } else if (reply.processData.eventPartyDays && invite.partyId && !reply.processData.eventPartyDays[invite.partyId]) {
        // may happen if there is a merged party - create a container otherwise the JSON would get messed up (array instead of an object)
        reply.processData.eventPartyDays[invite.partyId] = {};
    }
    let title;
    if (isInviteUpdate(invite)) {
        if (invite.status === JsonInviteInfoStatusEnum.Accepted) {
            title = t('Zpracování výzvy');
        } else {
            title = t('Náhled výzvy');
        }

    } else {
        if (invite.status === JsonInviteInfoStatusEnum.Accepted) {
            title = t('Zpracování pozvánky');
        } else {
            title = t('Náhled pozvánky');
        }
    }

    return <>
        <FormModal title={<span>{title} - {partyName(invite)} - {inviteTypeAllOptions.find(o => o.value === invite.inviteType)?.label} - <CodebookValue value={invite.eventId} name={'event'}/></span>}
            onCancel={props.onCancel}
            dialogClassName={'rsvp-review'}
            maxWidth={'lg'}>
            <InviteReviewForm {...props} invite={invite} item={reply} onEditInvite={handleEditInvite} onResendInvite={handleResendInvite}/>
        </FormModal>
        {!!editInvite && <InviteModal
            item={editInvite}
            onCancel={handleCancelInvite}
            onSave={handleSaveInvite}
        />}
    </>;
}

export default InviteReviewModal;
